import { useEffect, useMemo, useState } from 'react';
import { useInfiniteQuery } from 'react-query';

import { getShopperEmails } from 'apis/ShopRequestAPI';
import { useAppSelector } from 'store/hook';
import { PaginatedResponseType } from 'types/common';
import { useDebounce } from 'utils/hooks/useDebounce';

import { SHOPPER_EMAILS_PAGE_SIZE } from '../../constants';
import { ShopperEmailType, ShopRequestShopperEmailType } from '../../types';

type Props = {
  enabled: boolean;
  linkedShopperEmails: ShopRequestShopperEmailType[];
};

export const useShopperContactDirectory = ({ enabled, linkedShopperEmails }: Props) => {
  const currentUser = useAppSelector((state) => state.auth.currentUser);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300).trim();

  useEffect(() => {
    if (!enabled) {
      setSearch('');
    }
  }, [enabled]);

  const {
    data: accessibleShopperContactPages,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isFetchingNextPage,
  } = useInfiniteQuery<PaginatedResponseType<ShopperEmailType>>(
    ['shopper-emails', 'shop-request-link-directory-infinite', currentUser?.user_id, debouncedSearch],
    ({ pageParam = 1 }) =>
      getShopperEmails({
        page: pageParam,
        page_size: SHOPPER_EMAILS_PAGE_SIZE,
        search: debouncedSearch || undefined,
        ordering: '-created_at',
      }),
    {
      enabled,
      refetchOnWindowFocus: false,
      getNextPageParam: (lastPage, pages) => (lastPage.next ? pages.length + 1 : undefined),
    }
  );

  const accessibleShopperContacts = useMemo(
    () => accessibleShopperContactPages?.pages?.flatMap((page) => page.results) ?? [],
    [accessibleShopperContactPages]
  );
  const linkedShopperContactIds = useMemo(
    () =>
      new Set(
        linkedShopperEmails
          .map((shopperEmail) => shopperEmail.shopper_contact_id)
          .filter((shopperContactId): shopperContactId is number => Boolean(shopperContactId))
      ),
    [linkedShopperEmails]
  );
  const filteredLinkedShopperEmails = useMemo(() => {
    const normalizedSearch = debouncedSearch.toLowerCase();
    if (!normalizedSearch) {
      return linkedShopperEmails;
    }

    return linkedShopperEmails.filter((shopperEmail) =>
      [shopperEmail.name, shopperEmail.email, shopperEmail.phone, shopperEmail.label].some((value) =>
        value?.toLowerCase().includes(normalizedSearch)
      )
    );
  }, [debouncedSearch, linkedShopperEmails]);
  const availableShopperContacts = useMemo(
    () => accessibleShopperContacts.filter((shopperContact) => !linkedShopperContactIds.has(shopperContact.id)),
    [accessibleShopperContacts, linkedShopperContactIds]
  );

  return {
    availableShopperContacts,
    fetchNextPage,
    filteredLinkedShopperEmails,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    search,
    setSearch,
  };
};
